// English catalog — the source of truth for the string shape. `Strings` is
// `typeof en`, so every other locale (zh) must match it exactly: same keys,
// same function signatures. Add a string here first, then to zh.ts.

import type { Phase } from "../../data/tcbSchedule";

export const en = {
  lang: {
    label: "Language",
    en: "English",
    zh: "中文",
  },

  // Milk-volume unit labels (oz / ml). The caretaker picks which in Settings.
  units: {
    oz: "oz",
    ml: "ml",
  },

  common: {
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    loading: "Loading…",
    somethingWrong: "Something went wrong.",
    tryAgain: "Try again",
  },

  pwa: {
    updateReady: "A new version is ready.",
    reload: "Reload",
    later: "Later",
    offline: "Offline — your logs save here and sync when you're back.",
    pendingSync: (n: number) => `${n} change${n === 1 ? "" : "s"} waiting to sync`,
  },

  login: {
    title: "Baby Helper",
    subtitle: "Sign in to set up your baby's tracker.",
    google: "Continue with Google",
    googleSubmitting: "Opening Google…",
    or: "or",
    emailLabel: "Your email",
    emailPlaceholder: "you@example.com",
    emailHint: "We'll email you a 6-digit code — no password to remember.",
    submit: "Send me a code",
    submitting: "Sending…",
    sentTitle: "Check your email",
    sentBody: (email: string) =>
      `We emailed a 6-digit sign-in code to ${email}. Enter it below to finish signing in.`,
    codeLabel: "6-digit code",
    codePlaceholder: "123456",
    codeHint: "Typing the code keeps you signed in here — even when the email link opens in a browser.",
    verify: "Verify & sign in",
    verifying: "Verifying…",
    codeError: "That code didn't work — check the email, or send a new one.",
    resend: "Send a new code",
  },

  createBaby: {
    title: "Baby Helper",
    subtitle: "Track feeds, sleep, and weight — together with whoever's helping.",
    nameLabel: "Baby's name",
    namePlaceholder: "e.g. Eliza",
    birthdayLabel: "Birthday",
    birthdayHint: "If baby was preemie, you can set due date later.",
    yourNameLabel: "Your name",
    yourNamePlaceholder: "e.g. Mom",
    yourNameHint: "How you'll show up to others helping out.",
    submit: "Create",
    submitting: "Creating…",
    storageError: "Storage not configured yet. Ask the developer to set Supabase env vars.",
  },

  join: {
    title: "Join the tracker",
    subtitle: "You've been invited to help log feeds, sleep, and diapers.",
    google: "Continue with Google",
    googleSubmitting: "Opening Google…",
    googleHint: "Recommended — keeps you signed in even after installing the app.",
    anonAlt: "Or join without an account",
    anonHint: "Quick join — but you'll need a new invite if you switch devices.",
    nameLabel: "Your name",
    namePlaceholder: "e.g. Nanny",
    nameHint: "So the family knows who logged what.",
    submit: "Join",
    submitting: "Joining…",
    sessionError: "Couldn't start a session. Try again.",
    expired: "This invite link has expired — ask for a fresh one.",
    invalid: "This invite link isn't valid.",
    joinFailed: "Couldn't join. Try again in a moment.",
  },

  babyShell: {
    noAccessTitle: "No access",
    noAccessBody:
      "You don't have access to this tracker. Ask the family for a fresh invite link, or start your own.",
    startNew: "Start a new tracker",
    errorTitle: "Hmm.",
    startOver: "Start over",
    storageError: "Storage not configured yet.",
  },

  babyPicker: {
    title: "Which baby?",
    subtitle: "You're a caretaker on more than one tracker. Tap the one you want.",
    addBaby: "+ Set up another baby",
    noEntries: "No entries yet",
    never: "never",
    activity: (count: number, when: string) =>
      `${count} ${count === 1 ? "entry" : "entries"} · last logged ${when}`,
    minutesAgo: (n: number) => (n < 1 ? "just now" : `${n} min ago`),
    hoursAgo: (n: number) => `${n}h ago`,
    daysAgo: (n: number) => `${n} ${n === 1 ? "day" : "days"} ago`,
  },

  menu: {
    share: "Share",
    weight: "Weight",
    history: "History",
    settings: "Settings",
    switchBaby: "Switch baby",
  },

  settings: {
    accountSection: "Account",
    signOut: "Sign out",
    signingOut: "Signing out…",
    signOutFailed: "Could not sign out. Please try again.",

    deleteFailed: (msg: string) => `Could not delete: ${msg}`,
    back: "Back",
    title: "Settings",
    babySection: "Baby",
    photo: "Photo",
    name: "Name",
    babyNamePlaceholder: "Baby's name",
    birthday: "Birthday",
    dueDate: "Due date",
    feedingSection: "Feeding",
    bottleDefaultLabel: (unit: string) => `Bottle default (${unit})`,
    nurseDefaultLabel: (unit: string) => `Nursing default (${unit})`,
    notSet: "Not set",
    feedingHelper: "Quick chips log these amounts for everyone. Leave blank to reuse the last logged value.",
    youSection: "You",
    yourNamePlaceholder: "Your name",
    save: "Save",
    saved: "Saved",
    youAreCreator: (name: string) =>
      `You're the creator of ${name}'s tracker. This name is how others see who logged what.`,
    youAreCaretaker: (name: string) =>
      `You're a caretaker of ${name}'s tracker. This name is how others see who logged what.`,
    caretakersSection: "Caretakers",
    loading: "Loading…",
    youTag: "· you",
    roleCreator: "Creator",
    roleCaretaker: "Caretaker",
    remove: "Remove",
    sharingSection: "Sharing",
    inviteCaretaker: "Invite a caretaker",
    inviteHelper: "Send an invite link to your partner or nanny so they can log too.",
    displaySection: "Display",
    milkUnits: "Milk units",
    theme: "Theme",
    dangerSection: "Danger zone",
    deleteBaby: "Delete this baby",
    leaveTracker: "Leave this tracker",
    leaveArmed: "Tap again to leave",
    aboutSection: "About",
    version: "Version",
  },

  feedSheet: {
    title: "Feed",
    howMuch: "How much?",
    customAmount: (unit: string) => `Custom amount (${unit})`,
    logFeed: (amt: string) => `Log ${amt} feed`,
  },

  diaperSheet: {
    title: "Diaper",
    wet: "Wet",
    dirty: "Dirty",
    both: "Both",
  },

  pumpSheet: {
    title: "Pump",
    pumpedLabel: (unit: string) => `Pumped (${unit})`,
    durationLabel: "Duration (minutes, optional)",
    logPump: "Log pump",
  },

  noteSheet: {
    save: "Save",
  },

  weightSheetSub: {
    title: "Weight",
    caption: "From the pediatrician visit?",
    pounds: "Pounds",
    ounces: "Ounces",
    approx: (grams: number, lb: number, oz: number) => `≈ ${grams}g · ${lb}lb ${oz}oz`,
    logWeight: "Log weight",
  },

  weightEntry: {
    title: "Log new weight",
    lb: "lb",
    oz: "oz",
    lbOz: (lb: number, oz: number) => `${lb} lb ${oz} oz`,
    approxKg: (kg: string) => `≈ ${kg} kg`,
    cancel: "Cancel",
    save: "Save",
  },

  weightSheet: {
    weightLabel: "Weight",
    loggedRel: (rel: string) => `Logged ${rel}.`,
    noneYet: "No weight logged yet.",
    wasBefore: (weight: string, rel: string) => ` Was ${weight} ${rel}.`,
    recentLabel: "Recent weigh-ins",
    close: "Close",
    logNew: "Log new weight",
    sparklineEmpty: "Add a weight to see the trend.",
    insightEmpty: (babyName: string) =>
      `Log a weight to see growth + night-sleep guidance specific to ${babyName}.`,
    insightBuilding: (lb: number, babyName: string) =>
      `At ${lb} lb, ${babyName} is still building. Daytime feeds every 2–3 hours stay important — night stretching comes later.`,
    insightOnTrack: (lb: number, babyName: string, hours: number) =>
      `At ${lb} lb, ${babyName} is on track to start one longer overnight stretch. Aiming for around ${hours} hours as a target.`,
    insightConsolidating: (lb: number, babyName: string) =>
      `At ${lb} lb, ${babyName} is in a comfortable range to consolidate longer overnight stretches. We're allowing for one night feed up to about 4am instead of two.`,
    lbOz: (lb: number, oz: number) => `${lb} lb ${oz} oz`,
    relToday: "today",
    relYesterday: "yesterday",
    relDaysAgo: (days: number) => `${days} days ago`,
  },

  deleteConfirm: {
    title: (babyName: string) => `Delete ${babyName}'s tracker?`,
    body: "All logged feeds, naps, weights, and notes will be permanently removed. The share link will stop working. This can't be undone.",
    cancel: "Cancel",
    confirm: "Yes, delete everything.",
  },

  babyAvatar: {
    addPhoto: "Add photo",
    changePhoto: "Change photo",
    avatarLabel: (name: string) => `${name} avatar`,
  },

  composer: {
    chipWokeUp: "Wake now",
    chipNursing: (amt: string) => `Nursing ${amt}`,
    chipJustFed: (amt: string) => `Just fed ${amt}`,
    chipSleepingNow: "Sleep now",
    inputPlaceholder: 'Type to log — "fed 3 oz at 4am"',
    openLogAria: "Open log sheet",
    errParseRateLimited: "Daily AI limit reached. Type with the + button below.",
    errParseUnavailable: "AI parser is offline. Use the + button to log manually.",
    errParseFailed: "Could not parse. Try again or use the + button.",
    confirmNeedMore: "Need a bit more",
    confirmHeard: "I heard…",
    confirmNothing: "Nothing to log.",
    confirmCancel: "Cancel",
    confirmLooksRight: "Looks right",
    draftMethodBottle: "Bottle",
    draftMethodNursed: "Nursed",
    draftOz: (amt: string) => amt,
    draftMin: (min: number) => `${min}m`,
    draftBothSides: "both sides",
    draftSideLeft: "left",
    draftSideRight: "right",
    draftNow: "now",
    draftFeed: (summary: string, time: string) => `${summary} @ ${time}`,
    draftSleepLive: (time: string) => `Sleep started @ ${time}`,
    draftSlept: (start: string, end: string) => `Slept ${start}–${end}`,
    draftWoke: (time: string) => `Woke @ ${time}`,
    draftDiaper: (kind: string, time: string) => `Diaper: ${kind} @ ${time}`,
    draftWeight: (lb: number, oz: number, time: string) => `${lb} lb ${oz} oz @ ${time}`,
  },

  logSheet: {
    addNap: "Add nap",
    addNapSub: "Choose start & end times",
    bottle: "Bottle",
    bottleSub: (amt: string) => `${amt} · now`,
    breast: "Breast",
    breastSub: (amt: string) => `${amt} · now`,
    endNap: "End nap",
    endNapSub: "Wake her up",
    startNap: "Start a nap",
    startNapSub: "Live timer",
    diaper: "Diaper",
    diaperSub: "Wet · dirty",
    weight: "Weight",
    weightSub: "lb · oz",
    note: "Note",
    noteSub: "Jot anything down",
    cancel: "Cancel",
  },

  editEvent: {
    titleFeed: "Edit feed",
    titleNurse: "Edit nursing",
    titleSleep: "Edit sleep",
    titleDiaper: "Edit diaper",
    titleEntry: "Edit entry",
    fieldTime: "Time",
    fieldStarted: "Started",
    fieldEnded: "Ended",
    endedHint: "Leave blank if still asleep",
    fieldAmount: (unit: string) => `Amount (${unit})`,
    fieldDuration: "Duration (min)",
    fieldSide: "Side",
    sideLeft: "Left",
    sideRight: "Right",
    sideBoth: "Both",
    fieldKind: "Kind",
    kindWet: "Wet",
    kindDirty: "Dirty",
    kindBoth: "Both",
    cancel: "Cancel",
    save: "Save changes",
    deleteIdle: "Delete this entry",
    deleteArmed: "Tap again to delete",
  },

  // Backfill sheet — add a forgotten entry onto a past day.
  addEntry: {
    saveError: "Couldn’t save. Please try again.",
    fieldDate: "Date",
    title: "Add an entry",
    addButton: "Add an entry",
    fieldType: "What happened?",
    typeBottle: "Bottle",
    typeNursing: "Nursing",
    typeSleep: "Sleep",
    typeDiaper: "Diaper",
    typeNote: "Note",
    endRequiredHint: "When did it end? A past-day sleep needs an end time.",
    notePlaceholder: "Anything to remember?",
    save: "Add entry",
  },

  header: {
    todayDate: (date: string) => `Today, ${date}`,
    adjustedSuffix: " adjusted",
    prevDay: "Previous day",
    nextDay: "Next day",
    menu: "Menu",
    aboutBaby: (name: string) => `About ${name} — stage and what to expect`,
    addWeight: "Add weight",
    weightValue: (lb: number, oz: number) => `${lb} lb ${oz} oz`,
  },

  trackCards: {
    milk: "Milk",
    sleep: "Sleep",
    ozAmount: (amt: string) => amt,
    feedsToday: (count: number) => `${count} ${count === 1 ? "feed" : "feeds"} today`,
    napsToday: (count: number) => `${count} ${count === 1 ? "nap" : "naps"} today`,
    nextFeed: "Next feed",
    nextNap: "Next nap",
    nextBedtime: "Next bedtime",
    tryRange: (range: string) => `try ${range}`,
    tryAmount: (amt: string) => `try ${amt}`,
    feedOnCues: "feed on cues",
    feedOnHerCues: "feed on her cues",
    feedGuideline: (loHrs: number, hiHrs: number) => `every ${loHrs}–${hiHrs}h`,
    wakeGuideline: (loMin: number, hiMin: number) => `window ${loMin}–${hiMin}m`,
    sleepingNow: "Sleeping now",
    since: (time: string) => `since ${time}`,
    tapToEnd: "tap to end",
    tapToStart: "tap to start",
    cancel: "Cancel",
    confirmFeedTitle: (name: string) => `Log a feed for ${name}?`,
    confirmFeedBody: "Logs a feed right now — you can fine-tune the amount later from the timeline.",
    confirmFeedCta: "Yes, feeding",
    confirmStartNapTitle: (name: string) => `Start a nap for ${name}?`,
    confirmStartNapBody: "Starts a sleep timer from now.",
    confirmStartNapCta: "Yes, sleeping",
    confirmEndNapTitle: (name: string) => `End ${name}'s nap?`,
    confirmEndNapBody: "Marks the current nap as finished now.",
    confirmEndNapCta: "Yes, awake",
  },

  dayRibbon: {
    today: "Today",
    span: (start: string, end: string, hours: number) => `${start} → ${end} · ${hours}h`,
  },

  dayTimeline: {
    today: "Today",
    history: "History",
    now: "now",
    empty: "Nothing logged yet today.",
    titleFeed: "Feed",
    titleFed: "Fed",
    titleNursed: "Nursed",
    titleNap: "Nap",
    titleSleeping: "Sleeping",
    titleNightSleep: "Night sleep",
    titleBedtime: "Bedtime",
    titleDiaper: "Diaper",
    detailRangeOz: (range: string) => `~${range}`,
    detailApproxOz: (amt: string) => `~${amt}`,
    detailPredicted: "predicted",
    detailNursed: (min: number, amt: string) => `${min} min · ~${amt}`,
    detailOz: (amt: string) => amt,
    detailMin: (min: number) => `${min} min`,
    detailInProgress: "in progress",
    detailApproxMin: (min: number) => `~${min}m`,
    detailBedtimeRoutine: "routine ~30m before",
    diaperBoth: "wet & dirty",
  },

  pastDay: {
    dash: "—",
    intake: "Intake",
    daytimeSleep: "Daytime sleep",
    longestStretch: "Longest stretch",
    ozValue: (amt: string) => amt,
    events: "Events",
    empty: "Nothing logged this day.",
    labelBottle: "Bottle",
    labelNursed: "Nursed",
    labelNightSleep: "Night sleep",
    labelNap: "Nap",
    labelWake: "Wake",
    labelBedtime: "Bedtime",
    detailOz: (amt: string) => amt,
    detailMin: (min: number) => `${min}m`,
    thatNight: "That night",
    nightSummary: (feeds: number) => ` longest stretch · ${feeds} feed${feeds === 1 ? "" : "s"}`,
    feedsAt: (times: string) => ` · at ${times}`,
    weight: "Weight",
    weighedToday: (lb: number, oz: number) => `Weighed ${lb} lb ${oz} oz today.`,
  },

  guidance: {
    heading: "Guidance",
    wakeWindow: "Wake window",
    asleepNow: "asleep now",
    noNapsYet: "no naps logged yet",
    noRecentNap: "no recent nap",
    minsAwake: (mins: number) => `${mins}m awake`,
    feeds: "Feeds",
    feedEvery: (min: number, max: number) => `every ${min}–${max}h`,
    feedsSub: "day & night for now",
    daySleep: "Day sleep",
    pastGuide: "past the guide",
    withinGuide: "within the guide",
    noCapYet: "no cap yet",
    longestGap: "Longest gap",
    gapSpan: (from: string, to: string) => `${from} → ${to}`,
    noGapYet: "not enough feeds yet",
  },

  driftBanner: {
    dismiss: "Dismiss",
  },

  stage: {
    about: "About",
    age: "Age",
    weight: "Weight",
    stage: "Stage",
    wakeWindow: "Wake window",
    whatsHappening: "What's happening",
    whatToFocus: "What to focus on now",
    close: "Close",
  },

  share: {
    title: "Invite a caretaker",
    body: (name: string) =>
      `Anyone who opens this link can help track ${name} — log feeds, sleep, and diapers. Only you can change ${name}'s details or remove a caretaker. The link works for 30 days.`,
    preparing: "Preparing the invite link…",
    creatorOnly: "Only the baby's creator can make an invite link.",
    shareEllipsis: "Share…",
    close: "Close",
    copied: "Copied!",
    copyLink: "Copy link",
    shareTitle: (name: string) => `${name} · Baby Helper`,
    shareText: (name: string) => `Help track ${name}`,
  },

  history: {
    back: "Back",
    title: "This week",
    avgIntake: "Avg intake",
    avgDaytimeSleep: "Avg daytime sleep",
    avgLongestStretch: "Avg longest stretch",
    dayByDay: "Day by day",
    today: "Today",
    yesterday: "Yesterday",
    noSleep: "— sleep",
    overnight: (dur: string) => `${dur} overnight`,
    noOvernight: "— overnight",
    whatWeNoticed: "What we noticed",
    obsStretchLengthened: (fromHrs: number, toHrs: number) =>
      `Overnight stretches have lengthened. From about ${fromHrs} hours to about ${toHrs} hours over the past week.`,
  },

  // Adaptive coaching hints from the schedule engine (schedule.ts).
  hints: {
    daySleepTitle: "Lots of day sleep",
    daySleepDetail: (hrs: number, capHrs: number) =>
      `About ${hrs}h of daytime sleep so far — past the ~${capHrs}h guide for this age. Long day naps borrow from the night.`,
    wakeWindowExceededTitle: "Past the wake window",
    wakeWindowExceededDetail: (awakeMin: number, maxMin: number) =>
      `Awake ${awakeMin} min — a nap is usually due by ${maxMin} min at this age.`,
    wakeWindowNearTitle: "Getting close to nap time",
    wakeWindowNearDetail: (awakeMin: number, minMin: number, maxMin: number) =>
      `Awake ${awakeMin} min — wake window is ${minMin}–${maxMin}.`,
    feedOverdueHardTitle: "Time for a feed",
    feedOverdueHardDetail: (sinceHrs: string, maxHrs: number) =>
      `${sinceHrs} hr since the last feed — aim not to go longer than ${maxHrs} hr between daytime feeds.`,
    feedOverduePersonalTitle: "Due for a feed (vs typical)",
    feedOverduePersonalDetail: (sinceHrs: string, typicalHrs: string) =>
      `Last feed was ${sinceHrs}h ago — usually you're feeding by ${typicalHrs}h.`,
    clusterTitle: "Cluster feeding time",
    clusterDetail: "Short gaps between feeds are normal until bedtime — tank-up phase.",
    catnapTitle: "Consider a short catnap",
    catnapDetail: (sinceMin: number, bedtime: string) =>
      `Last nap ended ${sinceMin}min ago — a 15–45min catnap before bedtime ${bedtime} can bridge the gap.`,
    reverseCyclingTitle: "Most feeds are at night",
    reverseCyclingDetail: (nightPct: number) =>
      `Over the last 7 days, ${nightPct}% of feeds (bottle and nursing) happen at night. That's "reverse cycling" — try offering more full feeds during the day.`,
    nightStretchBehindTitle: "Night stretch is holding steady",
    nightStretchBehindDetail: (medHrs: number, targetHrs: number) =>
      `The last 7 nights average about ${medHrs}h, short of the ${targetHrs}h goal for this age. Keep bedtime consistent and steady — don't force it.`,
    rabTitle: "Long nursing session",
    rabDetail:
      "Watch for dozing during the feed (Rest at Breast). If a power-nap slipped in, it can wipe out the sleep drive for the next nap.",
    regressionTitle: "Night sleep dipped",
    regressionDetailReframe: (priorHrs: number, recentHrs: number) =>
      `Night sleep slipped from about ${priorHrs}h to ${recentHrs}h — this is the 4-month sleep reorganization. It's normal and it passes; hold bedtime steady.`,
    regressionDetailPlain: (priorHrs: number, recentHrs: number) =>
      `Night sleep slipped from about ${priorHrs}h to ${recentHrs}h over the last couple of nights. Keep the routine steady — it usually settles.`,
  },

  // Human label for each developmental phase (band.phase → display string).
  phaseLabel: {
    "supply-building": "Supply-building phase",
    foundation: "Foundation phase",
    stretching: "Stretching phase",
    "regression-prep": "Months 3–4 transition",
    "regression-active": "Active 4-month phase",
    "pre-sleep-training": "Ready for a full night",
  } satisfies Record<Phase, string>,
};
